import { useState } from 'react';
import axios from 'axios';
import { FloppyDisk, Tag } from '@phosphor-icons/react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const TYPES_ESPACE = [
  { v: 'etage', l: 'Niveau / Étage', icon: '🪜' },
  { v: 'appartement', l: 'Appartement / Logement', icon: '🏠' },
  { v: 'piece', l: 'Pièce', icon: '🚪' },
  { v: 'partie_commune', l: 'Partie commune', icon: '🛗' },
  { v: 'local_technique', l: 'Local technique', icon: '⚙️' },
  { v: 'cave', l: 'Cave', icon: '🕳️' },
  { v: 'parking', l: 'Parking', icon: '🅿️' },
  { v: 'local_commercial', l: 'Local commercial', icon: '🏪' },
  { v: 'exterieur', l: 'Extérieur', icon: '🌳' },
  { v: 'autre', l: 'Autre', icon: '📍' },
];

export function typeLabel(t) {
  return TYPES_ESPACE.find(x => x.v === t) || { l: t || '—', icon: '📍' };
}

export default function EspaceFormDialog({ chantierId, batimentId, espace, parentEspace, lots, onSaved, onClose }) {
  const isEdit = !!espace;
  const [form, setForm] = useState({
    type: espace?.type || (parentEspace ? 'piece' : 'etage'),
    nom: espace?.nom || '',
    niveau: espace?.niveau || (parentEspace?.niveau || 'RDC'),
    surface_totale: espace?.surface_totale ?? '',
    description: espace?.description || '',
    lots_ids: espace?.lots_ids || [],
  });
  const [saving, setSaving] = useState(false);

  const toggleLot = (lotId) => {
    setForm(prev => ({
      ...prev,
      lots_ids: prev.lots_ids.includes(lotId)
        ? prev.lots_ids.filter(x => x !== lotId)
        : [...prev.lots_ids, lotId],
    }));
  };

  const save = async () => {
    if (!form.nom.trim()) { toast.error('Le nom est obligatoire'); return; }
    setSaving(true);
    const payload = {
      type: form.type,
      nom: form.nom.trim(),
      niveau: form.niveau || 'RDC',
      ordre_niveau: 0,
      surface_totale: form.surface_totale === '' ? null : Number(form.surface_totale),
      description: form.description || null,
      lots_ids: form.lots_ids,
      parent_espace_id: parentEspace?.id || null,
    };
    try {
      if (isEdit) {
        await axios.put(`${API_URL}/api/chantiers/${chantierId}/espaces/${espace.id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/chantiers/${chantierId}/batiments/${batimentId}/espaces`, payload, { withCredentials: true });
      }
      toast.success(isEdit ? 'Espace modifié' : 'Espace ajouté');
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="espace-form-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'DM Serif Display, serif' }}>
            {isEdit ? 'Modifier l\'espace' : (parentEspace ? `Ajouter un sous-espace dans « ${parentEspace.nom} »` : 'Ajouter un espace')}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Nom *</Label>
            <Input value={form.nom} onChange={e => setForm({ ...form, nom: e.target.value })}
              placeholder="Cuisine, Salon, RDC, Appartement 1…" className="rounded-none mt-1" autoFocus
              data-testid="espace-form-nom" />
          </div>
          <div>
            <Label className="text-xs">Type</Label>
            <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}
              className="w-full mt-1 p-2 border border-[#E0E0DB] text-sm rounded-none"
              data-testid="espace-form-type">
              {TYPES_ESPACE.map(t => (<option key={t.v} value={t.v}>{t.icon} {t.l}</option>))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Niveau / Étage</Label>
              <Input value={form.niveau} onChange={e => setForm({ ...form, niveau: e.target.value })}
                placeholder="RDC, R+1, Sous-sol…" className="rounded-none mt-1 text-sm"
                data-testid="espace-form-niveau" />
            </div>
            <div>
              <Label className="text-xs">Surface (m², optionnel)</Label>
              <Input type="number" step="0.1" value={form.surface_totale}
                onChange={e => setForm({ ...form, surface_totale: e.target.value })}
                placeholder="—" className="rounded-none mt-1 text-sm"
                data-testid="espace-form-surface" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Notes (optionnel)</Label>
            <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
              rows={2} className="rounded-none mt-1 text-sm"
              data-testid="espace-form-description" />
          </div>
          {lots && lots.length > 0 && (
            <div>
              <Label className="text-xs flex items-center gap-1"><Tag size={12} /> Lot(s) rattaché(s)</Label>
              <div className="mt-2 max-h-40 overflow-y-auto border border-[#E0E0DB] p-2 space-y-1">
                {lots.map(lot => {
                  const checked = form.lots_ids.includes(lot.id);
                  return (
                    <label key={lot.id} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-[#FAFAF8] p-1">
                      <input type="checkbox" checked={checked} onChange={() => toggleLot(lot.id)}
                        className="accent-[#C75B39]" data-testid={`espace-form-lot-${lot.id}`} />
                      <span className="font-medium">{lot.lot_code || ''}</span>
                      <span>{lot.nom}</span>
                      {lot.entreprise_nom && <span className="ml-auto text-[#6B6B6B] italic">{lot.entreprise_nom}</span>}
                    </label>
                  );
                })}
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} className="rounded-none">Annuler</Button>
            <Button onClick={save} disabled={saving}
              className="bg-[#C75B39] hover:bg-[#D97B5C] rounded-none gap-2"
              data-testid="espace-form-save">
              <FloppyDisk size={14} /> {isEdit ? 'Enregistrer' : 'Ajouter'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
